import axios from "@/plugins/axios";
import utils from "@/stores/utils";

const state = {
    bundle: null,
    bundle_loading: false,
    bundle_loading_errors: null,
    bundle_saving: false,
    bundle_saving_errors: null,
    bundle_deleting: false,
    bundle_deleting_errors: null,
};

const mutations = {
    // Bundle mutations
    updateBundle(state, bundle) {
        state.bundle = bundle;
        state.bundle_loading = false;
        state.bundle_loading_errors = null;
    },

    updateBundleLoading(state, loading) {
        state.bundle_loading = loading;
    },

    updateBundleLoadingErrors(state, errors) {
        state.bundle_loading_errors = errors;
    },

    updateBundleSaving(state, saving) {
        state.bundle_saving = saving;
    },

    updateBundleSavingErrors(state, errors) {
        state.bundle_saving_errors = errors;
    },

    updateBundleDeleting(state, deleting) {
        state.bundle_deleting = deleting;
    },

    updateBundleDeletingErrors(state, errors) {
        state.bundle_deleting_errors = errors;
    },

    clearBundle(state) {
        state.bundle = null;
        state.bundle_loading_errors = null;
        state.bundle_saving_errors = null;
        state.bundle_deleting_errors = null;
    },
};

const actions = {
    // Initialisation
    async init({ commit }, params) {
        commit('clearBundle');
        commit('updateBundle', params.bundle);
    },

    // Bundle actions
    async fetchBundle({ commit }, bundleId) {
        commit('updateBundleLoading', true);
        try {
            const response = await axios.get(`/api/bundles/${bundleId}/`);
            commit('updateBundle', response.data);
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleLoadingErrors', errors);
            throw error;
        }
    },

    async saveBundle({ commit, state }, params) {
        commit('updateBundleSaving', true);
        try {
            let response;
            
            if (state.bundle?.id) {
                // Update existing bundle
                response = await axios.put(`/api/bundles/${state.bundle.id}/`, state.bundle);
            } else {
                // Create new bundle
                response = await axios.post('/api/bundles/', state.bundle);
            }
            commit('updateBundle', response.data);
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleSavingErrors', errors);
            throw error;
        } finally {
            commit('updateBundleSaving', false);
        }
    },


    async deleteBundle({ commit, dispatch }, bundleId) {
        commit('updateBundleDeleting', true);
        try {
            await axios.post(`/api/bundles/${bundleId}/archive/`);
            commit('updateBundle', null);
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleDeletingErrors', errors);
            throw error;
        } finally {
            commit('updateBundleSaving', false);
            dispatch("session/fetchStats", null, { root: true });
        }
    },

    async activateBundle({ commit }, bundleId) {
        commit('updateBundleSaving', true);
        try {
            const response = await axios.post(`/api/bundles/${bundleId}/activate/`);
            commit('updateBundle', response.data);
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleSavingErrors', errors);
            throw errors;
        } finally {
            commit('updateBundleSaving', false);
        }
    },

    async deactivateBundle({ commit }, bundleId) {
        commit('updateBundleSaving', true);
        try {
            const response = await axios.post(`/api/bundles/${bundleId}/deactivate/`);
            commit('updateBundle', response.data);
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleSavingErrors', errors);
            throw errors;
        } finally {
            commit('updateBundleSaving', false);
        }
    },

    // Utility actions
    clearBundle({ commit }) {
        commit('clearBundle');
    },

    setBundle({ commit }, bundle) {
        commit('updateBundle', bundle);
    },
};

const getters = {
    // Bundle getters
    bundle: state => state.bundle,
    bundle_loading: state => state.bundle_loading,
    bundle_loading_errors: state => state.bundle_loading_errors,
    bundle_saving: state => state.bundle_saving,
    bundle_saving_errors: state => state.bundle_saving_errors,
    bundle_deleting: state => state.bundle_deleting,
    bundle_deleting_errors: state => state.bundle_deleting_errors,

    // Computed getters
    hasBundle: state => !!state.bundle,
    isEditing: state => !!state.bundle && !!state.bundle.id,
    isCreating: state => !!state.bundle && !state.bundle.id,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
